import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import InContentAIForm from "components/ai_tools/InContentAIForm";
import ChatArea from "components/ai_tools/ChatArea";
import pluralize from "pluralize";
import LcFetch from "lib/shared/fetch";

import { useAccessTier } from "contexts/AccessTierContext";
import {
  AI_TOOL_CHANNELS,
  AI_TOOL_NAMES,
  QUESTION_MINIMUM_LENGTH
} from "./constants";
import useChatMessages from "./hooks/useChatMessages";

const InContentAskLitchartsAI = (props) => {
  const {
    aiAnswersCompletePath,
    guidesPath,
    inPoetry,
    infiniteAnswers,
    options: optionsFromProps,
    quotaRemaining: quotaFromProps,
    onQuotaChange,
    resetDate,
    resource,
    signupPath,
    submissionPath,
    toolName = AI_TOOL_NAMES.ASK_AI
  } = props;

  const [options, setOptions] = useState(optionsFromProps);
  const [resourceTitle, setResourceTitle] = useState(() => {
    return options.find((option) => option.value === resource)?.title;
  });

  const { isSignedOut, isAPlus } = useAccessTier();

  const eventCategory =
    toolName === AI_TOOL_NAMES.ASK_AI
      ? "Ask LitCharts AI on Content Pages"
      : "DQG on Content Pages";

  const {
    canFetchMoreAnswers,
    isStreaming,
    fetchAiAnswers,
    loadingPastMessages,
    quotaRemaining,
    messages,
    prevSlug,
    handleAskAIFormSubmit,
    handleQuizDiscussionEssayPrompt,
    canAskQuestions
  } = useChatMessages(
    {
      aiAnswersCompletePath,
      channel: AI_TOOL_CHANNELS[toolName],
      eventCategory,
      inContent: true,
      inPoetry,
      infiniteAnswers,
      locked: isSignedOut || quotaFromProps <= 0,
      onQuotaChange,
      quotaFromProps,
      resourceTitle,
      signupPath,
      submissionPath,
      toolName,
      upgradeable: false
    },
    [resourceTitle]
  );

  useEffect(() => {
    scrollToolIntoViewOnTap();
    fetchGuidesList();
  }, []);

  const handleResourceChange = (option) => {
    setResourceTitle(option.title);
  };

  const submissionMethod = () => {
    switch (toolName) {
      case AI_TOOL_NAMES.ASK_AI:
        return handleAskAIFormSubmit;
      case AI_TOOL_NAMES.DISCUSSION_QUESTIONS:
        return handleQuizDiscussionEssayPrompt;
      default:
        return handleAskAIFormSubmit;
    }
  };

  const fetchGuidesList = () => {
    LcFetch(guidesPath, { cache: "default" })
      .then((response) => {
        if (response.ok) return response.json();
        if (response.status === 304) return response.json();

        throw new Error(`Error fetching dropdown content: ${response.status}`);
      })
      .then((data) => {
        // This is a hack :(
        // When we replace `options` and pass it down to the form, and thus react-select, the dropdown is re-populated,
        // however, it no longer has the correct option selected. I suspect it's because the value we initially pass in
        // is no longer in the options array (pass by reference?). So we replace the identical entry in the new options
        // list with the one already set up.
        const newOptions = data.guides;
        const indexToReplace = newOptions.findIndex(
          (guide) => guide.value === options.at(0).value
        );
        newOptions[indexToReplace] = options.at(0);

        setOptions(newOptions);
      })
      .catch((error) => {
        console.error("Error fetching dropdown content:", error);
      });
  };

  const scrollToolIntoViewOnTap = () => {
    // Scrolls AI tool into view when header or body are tapped.
    const $scrollTarget = $(".ask-litcharts-ai--in-content-container");
    const $tapTarget = $(".ai-form--header, .chat-area--container");
    let offset;

    function scrollToTop(event) {
      if ($(event.target).is("a")) {
        return true;
      }

      let windowOffset = 120;
      if (inPoetry) {
        const $toolContainer = $("#poems-show__guide__ai-tool__container");
        windowOffset = $toolContainer.data().topOffset;
      }

      if (isSmallWindow()) {
        offset = $(".context-switch-banner:visible").offset().top;
      } else {
        offset = $scrollTarget.offset().top - windowOffset;
      }

      window.scrollTo({
        behavior: "smooth",
        top: offset
      });
    }

    $tapTarget.on("click", scrollToTop);
  };

  const isSmallWindow = () => {
    return $(".context-switch-banner:visible").length;
  };

  const resetText = `${pluralize(
    "answers",
    quotaRemaining,
    true
  )} left (resets on ${resetDate})`;

  return (
    <>
      <ChatArea
        canLoadMore={canFetchMoreAnswers}
        isStreaming={isStreaming}
        loadingPastMessages={loadingPastMessages}
        loadMore={fetchAiAnswers}
        messages={messages}
        userLoggedIn={props.userLoggedIn}
      />
      {!infiniteAnswers &&
      resetDate &&
      quotaRemaining === 0 &&
      !(toolName === AI_TOOL_NAMES.DISCUSSION_QUESTIONS && isAPlus) ? (
        <div className="ask-litcharts-ai--quota-remaining subtitle-2">
          <span>{resetText}</span>
        </div>
      ) : (
        <InContentAIForm
          toolName={toolName}
          {...props}
          loading={isStreaming || loadingPastMessages}
          onResourceChange={handleResourceChange}
          onSubmit={(formValues) => {
            submissionMethod()({
              ...formValues
            });
          }}
          options={options}
          canAskQuestions={canAskQuestions}
          // Note, please read before removing:
          // This line causes a re-render that clears out the "Question" field.
          key={`quota-remaining-${quotaRemaining}`}
          resetText={resetText}
          quotaRemaining={quotaRemaining}
          questionMinimum={QUESTION_MINIMUM_LENGTH}
          values={{ title: prevSlug || resource }}
        />
      )}
    </>
  );
};

InContentAskLitchartsAI.propTypes = {
  aiAnswersCompletePath: PropTypes.string,
  guidesPath: PropTypes.string,
  inPoetry: PropTypes.bool,
  infiniteAnswers: PropTypes.bool,
  locked: PropTypes.bool,
  onQuotaChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      title: PropTypes.string,
      value: PropTypes.string
    })
  ),
  questionMinimum: PropTypes.number,
  quotaRemaining: PropTypes.number.isRequired,
  resetDate: PropTypes.string,
  resource: PropTypes.string,
  signupPath: PropTypes.string,
  submissionPath: PropTypes.string,
  toolName: PropTypes.oneOf(Object.values(AI_TOOL_NAMES)).isRequired,
  userLoggedIn: PropTypes.bool
};

InContentAskLitchartsAI.defaultProps = {
  aiAnswersCompletePath: "/ai_answers/complete",
  guidesPath: "/ai/guides",
  inPoetry: false,
  infiniteAnswers: false,
  locked: false,
  onQuotaChange: null,
  options: [],
  questionMinimum: 25,
  resetDate: "",
  resource: null,
  signupPath: "/sign-up?focus=teacher",
  submissionPath: "/ai/ask-litcharts-ai",
  userLoggedIn: false
};

export default InContentAskLitchartsAI;
